import {
  Grid,
  Stack,
  TextField,
  Typography,
  Box,
  Button,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Chip,
  Collapse,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RemoveIcon from "@mui/icons-material/Remove";
import Divider from "@mui/material/Divider";
import FactoryDrag from "./FactoryDrag";
import { useApp } from "../../../../hooks/useApp";
import useMqttTest from "../../../../hooks/useMqttTest";
import Autocomplete from "@mui/material/Autocomplete";
import { useState, useCallback, useMemo } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import IconButton from "@mui/material/IconButton";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import FolderIcon from "@mui/icons-material/Folder";
import Tooltip from "@mui/material/Tooltip";
import ImageUpload from "../../../../components/ImageUpload";
import { SketchPicker } from "react-color";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { AgentEditor } from "../../../../components/AgentNode";
import { TagEditor } from "../../../../components/MediaFactory";

function buildTopicTree(messages) {
  const root = {};
  for (const msg of messages) {
    const segments = msg.topic.split('/');
    let node = root;
    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i];
      if (i === segments.length - 1) {
        if (!node[seg]) node[seg] = { __leaf: true, payload: msg.payload, timestamp: msg.timestamp };
        else {
          node[seg].payload = msg.payload;
          node[seg].timestamp = msg.timestamp;
        }
      } else {
        if (!node[seg] || node[seg].__leaf) node[seg] = {};
        node = node[seg];
      }
    }
  }
  return root;
}

function TopicTreeNode({ name, node, depth = 0 }) {
  const [open, setOpen] = useState(true);

  if (node.__leaf) {
    return (
      <Stack direction="row" alignItems="flex-start" sx={{ pl: depth * 2.5, py: 0.25 }}>
        <Box sx={{ width: 20, minWidth: 20, mt: '2px' }} />
        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
          {name}:{' '}
          <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>
            {node.payload}
          </Box>
          <Box component="span" sx={{ color: 'text.disabled', ml: 1, fontSize: '0.7rem' }}>
            {node.timestamp.toLocaleTimeString()}
          </Box>
        </Typography>
      </Stack>
    );
  }

  const children = Object.keys(node).filter((k) => k !== '__leaf');

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        onClick={() => setOpen((v) => !v)}
        sx={{
          pl: depth * 2.5,
          py: 0.25,
          cursor: 'pointer',
          borderRadius: 0.5,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <ChevronRightIcon
          sx={{
            fontSize: 16,
            transition: 'transform 150ms',
            transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
            mr: 0.25,
          }}
        />
        {open ? (
          <FolderOpenIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
        ) : (
          <FolderIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
        )}
        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
          {name}
        </Typography>
      </Stack>
      <Collapse in={open}>
        {children.map((key) => (
          <TopicTreeNode key={key} name={key} node={node[key]} depth={depth + 1} />
        ))}
      </Collapse>
    </Box>
  );
}

function MqttTopicTree({ messages }) {
  const tree = useMemo(() => buildTopicTree(messages), [messages]);
  const rootKeys = Object.keys(tree);

  if (rootKeys.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary" sx={{ p: 1.5 }}>
        No messages received yet...
      </Typography>
    );
  }

  return (
    <Box sx={{ py: 0.5 }}>
      {rootKeys.map((key) => (
        <TopicTreeNode key={key} name={key} node={tree[key]} depth={0} />
      ))}
    </Box>
  );
}

export default function FactoryThree({ asset, setAsset }) {
  const { combinedAssets } = useApp();
  const [dropDown, setDropDown] = useState(null);
  const [openPickerIndex, setOpenPickerIndex] = useState(null);
  const [dataSourcePromptOpen, setDataSourcePromptOpen] = useState(false);
  const [pendingAddType, setPendingAddType] = useState(null);
  const [tagEditorApi, setTagEditorApi] = useState(null);
  const [agentEditorApi, setAgentEditorApi] = useState(null);
  const [showMessages, setShowMessages] = useState(false);
  const { status: testStatus, error: testError, messages: testMessages, startTest, stopTest } = useMqttTest();
  const [showUnsMessages, setShowUnsMessages] = useState(false);
  const { status: unsTestStatus, error: unsTestError, messages: unsTestMessages, startTest: unsStartTest, stopTest: unsStopTest } = useMqttTest();

  const dataSource = asset?.dataSource || "";
  const isLive = dataSource === "live";
  const hasDataTagsOrAgents = (asset?.dataTags?.length || 0) > 0 || (asset?.agents?.length || 0) > 0;

  // Group-level data sources (inherit from factory if empty)
  const agentGroupDataSource = asset?.agentGroupDataSource || "";
  const dataTagGroupDataSource = asset?.dataTagGroupDataSource || "";
  const effectiveAgentGroupSource = agentGroupDataSource || dataSource;
  const effectiveTagGroupSource = dataTagGroupDataSource || dataSource;

  // Show MQTT config if any level resolves to "live"
  const hasAnyLiveSource = isLive
    || agentGroupDataSource === "live"
    || dataTagGroupDataSource === "live"
    || (asset?.agents || []).some((a) => a.dataSource === "live")
    || (asset?.dataTags || []).some((t) => t.dataSource === "live");

  const requireDataSource = useCallback((addType) => {
    if (!dataSource) {
      setPendingAddType(addType);
      setDataSourcePromptOpen(true);
      return false;
    }
    return true;
  }, [dataSource]);

  const handleDataSourcePromptChoice = (choice) => {
    const addType = pendingAddType;
    setAsset((prev) => ({ ...prev, dataSource: choice }));
    setDataSourcePromptOpen(false);
    setPendingAddType(null);

    setTimeout(() => {
      if (addType === "tag") {
        tagEditorApi?.openAddDialog();
      } else if (addType === "agent") {
        agentEditorApi?.openAddDialog();
      }
    }, 0);
  };

  const ensureChildrenArray = (currentAsset) => {
    return {
      ...currentAsset,
      Children: Array.isArray(currentAsset.Children)
        ? currentAsset.Children
        : [],
    };
  };

  function handleChange(event) {
    const newAsset = { ...asset, [event.target.id]: event.target.value };
    setAsset(newAsset);
  }

  function handleAdd() {
    const updatedAsset = ensureChildrenArray(asset);

    if (!dropDown) {
      alert("Please select an asset before adding.");
      return;
    }

    if (updatedAsset.Children.some((child) => child.id === dropDown.id)) {
      alert("This asset is already added.");
      return;
    }

    const newChild = {
      id: dropDown.id,
      Title: dropDown.Title,
      Left: "50%",
      Top: "50%",
      flow: false,
      color: "#b0e0e6",
    };

    setAsset((prevAsset) => ({
      ...ensureChildrenArray(prevAsset),
      Children: [...ensureChildrenArray(prevAsset).Children, newChild],
    }));

    setDropDown(null);
  }

  const moveAsset = (index, direction) => {
    const updatedAsset = ensureChildrenArray(asset);
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex >= 0 && newIndex < updatedAsset.Children.length) {
      const newChildren = [...updatedAsset.Children];
      const [movedItem] = newChildren.splice(index, 1);
      newChildren.splice(newIndex, 0, movedItem);
      setAsset({ ...updatedAsset, Children: newChildren });
    }
  };

  const handleFileUploadChange = (file) => {
    const newAsset = { ...asset, img: file };
    setAsset(newAsset);
  };

  const handleChildColorChange = (index, color) => {
    setAsset((prev) => {
      const updatedPrev = ensureChildrenArray(prev);

      const newChildren = updatedPrev.Children.map((child, i) =>
        i === index ? { ...child, color } : child
      );

      const result = { ...updatedPrev, Children: newChildren };

      return result;
    });
  };

  const handleAgentsChange = (newAgents) => {
    setAsset((prev) => {
      const noTagsOrAgents = newAgents.length === 0 && (prev.dataTags?.length || 0) === 0;
      const noAgents = newAgents.length === 0;
      return {
        ...prev,
        agents: newAgents,
        ...(noTagsOrAgents && { dataSource: "", agentGroupDataSource: "", dataTagGroupDataSource: "" }),
        ...(noAgents && !noTagsOrAgents && { agentGroupDataSource: "" }),
      };
    });
  };

  const handleTagsChange = (newTags) => {
    setAsset((prev) => {
      const noTagsOrAgents = newTags.length === 0 && (prev.agents?.length || 0) === 0;
      const noTags = newTags.length === 0;
      return {
        ...prev,
        dataTags: newTags,
        ...(noTagsOrAgents && { dataSource: "", agentGroupDataSource: "", dataTagGroupDataSource: "" }),
        ...(noTags && !noTagsOrAgents && { dataTagGroupDataSource: "" }),
      };
    });
  };

  const handleDataSourceChange = (event, newValue) => {
    if (newValue !== null) {
      setAsset((prev) => ({ ...prev, dataSource: newValue }));
    }
  };

  const handleAgentGroupDataSourceChange = (event, newValue) => {
    if (newValue !== null) {
      setAsset((prev) => ({
        ...prev,
        agentGroupDataSource: newValue === "inherit" ? "" : newValue,
      }));
    }
  };

  const handleTagGroupDataSourceChange = (event, newValue) => {
    if (newValue !== null) {
      setAsset((prev) => ({
        ...prev,
        dataTagGroupDataSource: newValue === "inherit" ? "" : newValue,
      }));
    }
  };

  const handleMqttFieldChange = (field, value) => {
    setAsset((prev) => ({
      ...prev,
      mqttConfig: { ...prev.mqttConfig, [field]: value },
    }));
  };

  return (
    <Grid container sx={{ flexDirection: "row", gap: 4 }}>
      <Grid
        item
        sx={{
          justifyContent: "center",
          alignItems: "center",
          width: "fit-content",
        }}
        xs={12}
        xl={8}
      >
        <FactoryDrag asset={asset} setAsset={setAsset} />

        {/* Factory-level Data Source Toggle - only shown when tags or agents exist */}
        {hasDataTagsOrAgents && <Box sx={{ mt: 3, p: 2, border: "1px solid #BDBDBD", borderRadius: 2 }}>
          <Typography fontWeight="bold" sx={{ mb: 1 }}>
            Data Source
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
            Choose whether this factory uses live MQTT data or simulated data for agents and data tags.
          </Typography>
          <ToggleButtonGroup
            value={dataSource}
            exclusive
            onChange={handleDataSourceChange}
            size="small"
          >
            <ToggleButton value="live">Live MQTT</ToggleButton>
            <ToggleButton value="simulated">Simulated</ToggleButton>
          </ToggleButtonGroup>

          {/* MQTT Configuration - shown when any node could be live */}
          {hasAnyLiveSource && (
            <Box sx={{ mt: 2 }}>
              {!asset?.mqttConfig?.broker && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  MQTT configuration is required for live data
                </Alert>
              )}
              <Typography fontWeight="bold" sx={{ mb: 1.5 }}>
                MQTT Configuration
              </Typography>
              <Stack direction="column" gap={2}>
                <TextField
                  label="Broker URL"
                  size="small"
                  fullWidth
                  value={asset?.mqttConfig?.broker || ""}
                  onChange={(e) => handleMqttFieldChange("broker", e.target.value)}
                  placeholder="wss://your-broker.com:8884/mqtt"
                  helperText="WebSocket URL of your MQTT broker"
                />
                <TextField
                  label="Username"
                  size="small"
                  fullWidth
                  value={asset?.mqttConfig?.username || ""}
                  onChange={(e) => handleMqttFieldChange("username", e.target.value)}
                />
                <TextField
                  label="Password"
                  type="password"
                  size="small"
                  fullWidth
                  value={asset?.mqttConfig?.password || ""}
                  onChange={(e) => handleMqttFieldChange("password", e.target.value)}
                />
                <TextField
                  label="Topic Prefix"
                  size="small"
                  fullWidth
                  value={asset?.mqttConfig?.topicPrefix || ""}
                  onChange={(e) => handleMqttFieldChange("topicPrefix", e.target.value)}
                  placeholder="Tulip/Boston/tulip_experience_center"
                  helperText="Common prefix for all topic paths"
                />
              </Stack>

              {/* Test Connection */}
              <Box sx={{ mt: 2 }}>
                <Stack direction="row" alignItems="center" gap={1.5}>
                  {testStatus === 'idle' || testStatus === 'error' ? (
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={!asset?.mqttConfig?.broker}
                      onClick={() => startTest(asset.mqttConfig)}
                    >
                      Test Connection
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      color="warning"
                      size="small"
                      onClick={stopTest}
                    >
                      Stop Test
                    </Button>
                  )}

                  {testStatus === 'connecting' && (
                    <Chip
                      icon={<CircularProgress size={14} />}
                      label="Connecting..."
                      size="small"
                      variant="outlined"
                    />
                  )}
                  {testStatus === 'connected' && (
                    <Chip
                      label={`Connected — ${testMessages.length} message${testMessages.length !== 1 ? 's' : ''}`}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  )}
                </Stack>

                {testStatus === 'error' && testError && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {testError}
                  </Alert>
                )}

                {testStatus === 'connected' && (
                  <Alert severity="success" sx={{ mt: 1 }}>
                    Successfully connected to broker. Listening on{" "}
                    <strong>
                      {(() => {
                        const p = (asset?.mqttConfig?.topicPrefix || '').replace(/\/+$/, '');
                        if (!p) return '#';
                        return p.endsWith('#') ? p : `${p}/#`;
                      })()}
                    </strong>
                    {" "}(auto-stops after 30s)
                  </Alert>
                )}

                {(testStatus === 'connected' || testMessages.length > 0) && (
                  <Box sx={{ mt: 1 }}>
                    <Button
                      size="small"
                      onClick={() => setShowMessages((v) => !v)}
                      sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
                    >
                      {showMessages ? 'Hide' : 'Show'} topic tree ({testMessages.length} message{testMessages.length !== 1 ? 's' : ''})
                    </Button>
                    <Collapse in={showMessages}>
                      <Box
                        sx={{
                          mt: 1,
                          maxHeight: 300,
                          overflow: 'auto',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          px: 0.5,
                        }}
                      >
                        <MqttTopicTree messages={testMessages} />
                        {testMessages.length >= 50 && (
                          <Typography variant="caption" color="textSecondary" sx={{ p: 1, display: 'block' }}>
                            Message limit reached (50). Stop and restart to capture more.
                          </Typography>
                        )}
                      </Box>
                    </Collapse>
                  </Box>
                )}
              </Box>

            </Box>
          )}
        </Box>}
      </Grid>

      <Grid item xs>
        <Stack direction={"column"} gap={1} sx={{ height: "100%" }}>
          <Typography fontWeight={"bold"}>Factory Details</Typography>

          <TextField
            id="Title"
            label="Title"
            fullWidth
            size="small"
            value={asset.Title}
            onChange={handleChange}
          />

          <ImageUpload value={asset.img} onChange={handleFileUploadChange} acceptVideo />

          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="caption" color="textSecondary" sx={{ whiteSpace: "nowrap" }}>
              Zoom
            </Typography>
            <RemoveIcon
              fontSize="small"
              sx={{ cursor: "pointer", color: "text.secondary", fontSize: 16 }}
              onClick={() => setAsset((prev) => ({ ...prev, imageScale: Math.max(0.5, +((prev.imageScale || 1) - 0.1).toFixed(1)) }))}
            />
            <Slider
              value={asset.imageScale || 1}
              min={0.5}
              max={3}
              step={0.1}
              onChange={(_, val) => setAsset((prev) => ({ ...prev, imageScale: val }))}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => `${Math.round(v * 100)}%`}
              size="small"
              sx={{ flex: 1, minWidth: 80 }}
            />
            <AddIcon
              fontSize="small"
              sx={{ cursor: "pointer", color: "text.secondary", fontSize: 16 }}
              onClick={() => setAsset((prev) => ({ ...prev, imageScale: Math.min(3, +((prev.imageScale || 1) + 0.1).toFixed(1)) }))}
            />
            <Typography variant="caption" color="textSecondary" sx={{ minWidth: 32, textAlign: "center" }}>
              {Math.round((asset.imageScale || 1) * 100)}%
            </Typography>
          </Stack>

          {hasAnyLiveSource && (
            <Accordion sx={{ mt: 1, borderRadius: 2, "&:before": { display: "none" } }} defaultExpanded={false}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="bold">Video Playback Control</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
                  Control the background video via an MQTT topic. Leave empty to autoplay continuously.
                </Typography>
                <Stack direction="column" gap={2}>
                  <TextField
                    label="Video Control Topic"
                    size="small"
                    fullWidth
                    value={asset?.videoControlTopic || ""}
                    onChange={(e) =>
                      setAsset((prev) => ({ ...prev, videoControlTopic: e.target.value }))
                    }
                    placeholder="Enterprise B/Site1/Area1/Line1/Status"
                    helperText="MQTT topic whose value controls video playback"
                  />
                  <Stack direction="row" gap={2}>
                    <TextField
                      label="Start Value"
                      size="small"
                      fullWidth
                      value={asset?.videoStartValue || ""}
                      onChange={(e) =>
                        setAsset((prev) => ({ ...prev, videoStartValue: e.target.value }))
                      }
                      placeholder="Running"
                      helperText="Value that starts the video"
                    />
                    <TextField
                      label="Stop Value"
                      size="small"
                      fullWidth
                      value={asset?.videoStopValue || ""}
                      onChange={(e) =>
                        setAsset((prev) => ({ ...prev, videoStopValue: e.target.value }))
                      }
                      placeholder="Stopped"
                      helperText="Value that pauses the video"
                    />
                  </Stack>
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}

          {/* UNS Sidebar Configuration */}
          <Accordion
            sx={{ mt: 1, borderRadius: 2, "&:before": { display: "none" } }}
            defaultExpanded={!!asset.unsConfig?.enabled}
            TransitionProps={{ timeout: 0 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography fontWeight="bold">UNS Sidebar</Typography>
                {asset.unsConfig?.enabled && (
                  <Chip label="Enabled" size="small" color="success" variant="outlined" />
                )}
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={asset.unsConfig?.enabled ? { height: 500, overflow: "auto" } : {}}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
                Optionally display a live MQTT topic tree panel alongside the factory.
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={!!asset.unsConfig?.enabled}
                    onChange={(e) => {
                      const enabling = e.target.checked;
                      setAsset((prev) => {
                        const existing = prev.unsConfig || {};
                        const needsDefaultBranch = enabling && !(existing.topicBranches?.length > 0);
                        return {
                          ...prev,
                          unsConfig: {
                            ...existing,
                            enabled: enabling,
                            ...(needsDefaultBranch && { topicBranches: ["#"] }),
                          },
                        };
                      });
                    }}
                  />
                }
                label="Enable UNS Sidebar"
              />

              {asset.unsConfig?.enabled && (
                <Stack direction="column" gap={2} sx={{ mt: 1 }}>
                  <Typography fontWeight="bold">MQTT Configuration</Typography>
                  <TextField
                    label="Broker URL"
                    size="small"
                    fullWidth
                    value={asset.unsConfig?.mqttConfig?.broker || ""}
                    onChange={(e) =>
                      setAsset((prev) => ({
                        ...prev,
                        unsConfig: {
                          ...prev.unsConfig,
                          mqttConfig: { ...prev.unsConfig?.mqttConfig, broker: e.target.value },
                        },
                      }))
                    }
                    placeholder="wss://your-broker.com:8884/mqtt"
                    helperText="WebSocket URL of your MQTT broker"
                  />
                  <TextField
                    label="Username"
                    size="small"
                    fullWidth
                    value={asset.unsConfig?.mqttConfig?.username || ""}
                    onChange={(e) =>
                      setAsset((prev) => ({
                        ...prev,
                        unsConfig: {
                          ...prev.unsConfig,
                          mqttConfig: { ...prev.unsConfig?.mqttConfig, username: e.target.value },
                        },
                      }))
                    }
                  />
                  <TextField
                    label="Password"
                    type="password"
                    size="small"
                    fullWidth
                    value={asset.unsConfig?.mqttConfig?.password || ""}
                    onChange={(e) =>
                      setAsset((prev) => ({
                        ...prev,
                        unsConfig: {
                          ...prev.unsConfig,
                          mqttConfig: { ...prev.unsConfig?.mqttConfig, password: e.target.value },
                        },
                      }))
                    }
                  />
                  <TextField
                    label="Topic Prefix"
                    size="small"
                    fullWidth
                    value={asset.unsConfig?.mqttConfig?.topicPrefix || ""}
                    onChange={(e) =>
                      setAsset((prev) => ({
                        ...prev,
                        unsConfig: {
                          ...prev.unsConfig,
                          mqttConfig: { ...prev.unsConfig?.mqttConfig, topicPrefix: e.target.value },
                        },
                      }))
                    }
                    placeholder="MyEnterprise"
                    helperText="Common prefix for all topic paths"
                  />

                  {/* Test Connection */}
                  <Box sx={{ mt: 1 }}>
                    <Stack direction="row" alignItems="center" gap={1.5}>
                      {unsTestStatus === 'idle' || unsTestStatus === 'error' ? (
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={!asset.unsConfig?.mqttConfig?.broker}
                          onClick={() => unsStartTest(asset.unsConfig.mqttConfig)}
                        >
                          Test Connection
                        </Button>
                      ) : (
                        <Button
                          variant="outlined"
                          color="warning"
                          size="small"
                          onClick={unsStopTest}
                        >
                          Stop Test
                        </Button>
                      )}

                      {unsTestStatus === 'connecting' && (
                        <Chip
                          icon={<CircularProgress size={14} />}
                          label="Connecting..."
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {unsTestStatus === 'connected' && (
                        <Chip
                          label={`Connected — ${unsTestMessages.length} message${unsTestMessages.length !== 1 ? 's' : ''}`}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      )}
                    </Stack>

                    {unsTestStatus === 'error' && unsTestError && (
                      <Alert severity="error" sx={{ mt: 1 }}>
                        {unsTestError}
                      </Alert>
                    )}

                    {unsTestStatus === 'connected' && (
                      <Alert severity="success" sx={{ mt: 1 }}>
                        Successfully connected to broker. Listening on{" "}
                        <strong>
                          {(() => {
                            const p = (asset.unsConfig?.mqttConfig?.topicPrefix || '').replace(/\/+$/, '');
                            if (!p) return '#';
                            return p.endsWith('#') ? p : `${p}/#`;
                          })()}
                        </strong>
                        {" "}(auto-stops after 30s)
                      </Alert>
                    )}

                    {(unsTestStatus === 'connected' || unsTestMessages.length > 0) && (
                      <Box sx={{ mt: 1 }}>
                        <Button
                          size="small"
                          onClick={() => setShowUnsMessages((v) => !v)}
                          sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
                        >
                          {showUnsMessages ? 'Hide' : 'Show'} topic tree ({unsTestMessages.length} message{unsTestMessages.length !== 1 ? 's' : ''})
                        </Button>
                        <Collapse in={showUnsMessages}>
                          <Box
                            sx={{
                              mt: 1,
                              maxHeight: 300,
                              overflow: 'auto',
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              px: 0.5,
                            }}
                          >
                            <MqttTopicTree messages={unsTestMessages} />
                            {unsTestMessages.length >= 50 && (
                              <Typography variant="caption" color="textSecondary" sx={{ p: 1, display: 'block' }}>
                                Message limit reached (50). Stop and restart to capture more.
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </Box>
                    )}
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  <Typography fontWeight="bold">Topic Branches</Typography>
                  <Typography variant="body2" color="textSecondary">
                    MQTT topic patterns to subscribe to. Use # for wildcards. These define the "Overall" view.
                  </Typography>
                  {(asset.unsConfig?.topicBranches || []).map((branch, index) => (
                    <Stack key={index} direction="row" gap={1} alignItems="center">
                      <TextField
                        size="small"
                        fullWidth
                        value={branch}
                        onChange={(e) => {
                          const newBranches = [...(asset.unsConfig?.topicBranches || [])];
                          newBranches[index] = e.target.value;
                          setAsset((prev) => ({
                            ...prev,
                            unsConfig: { ...prev.unsConfig, topicBranches: newBranches },
                          }));
                        }}
                        placeholder="MyEnterprise/Site1/#"
                      />
                      <IconButton
                        size="small"
                        onClick={() => {
                          const newBranches = (asset.unsConfig?.topicBranches || []).filter(
                            (_, i) => i !== index
                          );
                          setAsset((prev) => ({
                            ...prev,
                            unsConfig: { ...prev.unsConfig, topicBranches: newBranches },
                          }));
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const newBranches = [...(asset.unsConfig?.topicBranches || []), ""];
                      setAsset((prev) => ({
                        ...prev,
                        unsConfig: { ...prev.unsConfig, topicBranches: newBranches },
                      }));
                    }}
                  >
                    Add Topic Branch
                  </Button>

                  <Divider sx={{ my: 1 }} />

                  <Typography fontWeight="bold">Site Filters (optional)</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Add filters to let users narrow the tree to specific branches. When empty, the sidebar shows all topic branches with no dropdown.
                  </Typography>
                  {(asset.unsConfig?.siteFilters || []).map((filter, index) => (
                    <Box key={index} sx={{ border: "1px solid #ccc", borderRadius: 1, p: 1.5 }}>
                      <Stack direction="column" gap={1}>
                        <Stack direction="row" gap={1} alignItems="center">
                          <TextField
                            label="Label"
                            size="small"
                            fullWidth
                            value={filter.label || ""}
                            onChange={(e) => {
                              const newFilters = [...(asset.unsConfig?.siteFilters || [])];
                              newFilters[index] = {
                                ...newFilters[index],
                                label: e.target.value,
                                value: e.target.value.toLowerCase().replace(/\s+/g, "-"),
                              };
                              setAsset((prev) => ({
                                ...prev,
                                unsConfig: { ...prev.unsConfig, siteFilters: newFilters },
                              }));
                            }}
                            placeholder="Site 1"
                          />
                          <IconButton
                            size="small"
                            onClick={() => {
                              const newFilters = (asset.unsConfig?.siteFilters || []).filter(
                                (_, i) => i !== index
                              );
                              setAsset((prev) => ({
                                ...prev,
                                unsConfig: { ...prev.unsConfig, siteFilters: newFilters },
                              }));
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                        <TextField
                          label="Topic Pattern"
                          size="small"
                          fullWidth
                          value={filter.topicPattern || ""}
                          onChange={(e) => {
                            const newFilters = [...(asset.unsConfig?.siteFilters || [])];
                            newFilters[index] = { ...newFilters[index], topicPattern: e.target.value };
                            setAsset((prev) => ({
                              ...prev,
                              unsConfig: { ...prev.unsConfig, siteFilters: newFilters },
                            }));
                          }}
                          placeholder="MyEnterprise/Site1/#"
                          helperText="MQTT topic pattern with wildcard"
                        />
                      </Stack>
                    </Box>
                  ))}
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const newFilters = [
                        ...(asset.unsConfig?.siteFilters || []),
                        { label: "", value: "", topicPattern: "" },
                      ];
                      setAsset((prev) => ({
                        ...prev,
                        unsConfig: { ...prev.unsConfig, siteFilters: newFilters },
                      }));
                    }}
                  >
                    Add Site Filter
                  </Button>

                  {(asset.unsConfig?.siteFilters || []).length > 0 && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <FormControl fullWidth size="small">
                        <InputLabel>Default Filter</InputLabel>
                        <Select
                          label="Default Filter"
                          value={asset.unsConfig?.defaultFilter || "overall"}
                          onChange={(e) =>
                            setAsset((prev) => ({
                              ...prev,
                              unsConfig: { ...prev.unsConfig, defaultFilter: e.target.value },
                            }))
                          }
                        >
                          <MenuItem value="overall">Overall (all branches)</MenuItem>
                          {(asset.unsConfig?.siteFilters || []).map((f) =>
                            f.value ? (
                              <MenuItem key={f.value} value={f.value}>
                                {f.label || f.value}
                              </MenuItem>
                            ) : null
                          )}
                        </Select>
                      </FormControl>
                    </>
                  )}
                </Stack>
              )}
            </AccordionDetails>
          </Accordion>

          <Typography sx={{ mt: 2 }}>Assets</Typography>
          <Stack direction={"row"}>
            {combinedAssets && combinedAssets.length > 0 && (
              <Autocomplete
                disablePortal
                id="factorySelect"
                options={combinedAssets.filter(
                  (asset) =>
                    asset.Type &&
                    ["app", "beacon", "video"].includes(asset.Type)
                )}
                getOptionLabel={(option) => option.Title || ""}
                isOptionEqualToValue={(option, value) =>
                  option.id === value?.id
                }
                sx={{ width: "100%" }}
                size="small"
                value={dropDown}
                onChange={(event, newValue) => {
                  setDropDown(newValue);
                }}
                renderOption={(props, option) => (
                  <li {...props} key={option.id || `${option.Title}-${option.Type}`}>
                    {option.Title || ""}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Title" />
                )}
              />
            )}
            <Button onClick={handleAdd}>Add</Button>
          </Stack>

          <Box
            sx={{
              border: "1px solid #BDBDBD",
              height: "100%",
              paddingBlock: 2,
              paddingInline: 1,
              borderRadius: 2,
            }}
          >
            {asset.Children &&
              asset.Children.length > 0 &&
              asset.Children.map((child, thisIndex) => {
                const isOpen = openPickerIndex === thisIndex;
                return (
                  <Stack
                    key={thisIndex}
                    direction={"column"}
                    spacing={1}
                    marginBottom={2}
                    borderBottom={1}
                    borderColor={"#BDBDBD"}
                    pb={1}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          onClick={() =>
                            setOpenPickerIndex(isOpen ? null : thisIndex)
                          }
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: "4px",
                            backgroundColor: child.color || "#b0e0e6",
                            border: "1px solid #ccc",
                            cursor: "pointer",
                            flexShrink: 0,
                          }}
                        />
                        <Typography>{child.Title}</Typography>
                      </Box>
                      <div className="flex align-items-center">
                        {child.flow && (
                          <>
                            <IconButton
                              aria-label="down"
                              size="small"
                              onClick={() => moveAsset(thisIndex, "down")}
                              disabled={thisIndex === asset.Children.length - 1}
                            >
                              <ArrowDownwardIcon fontSize={"small"} />
                            </IconButton>
                            <IconButton
                              aria-label="up"
                              size="small"
                              onClick={() => moveAsset(thisIndex, "up")}
                              disabled={thisIndex === 0}
                            >
                              <ArrowUpwardIcon fontSize={"small"} />
                            </IconButton>
                          </>
                        )}
                        <Tooltip title="Flow" placement="left">
                          <Checkbox
                            checked={child.flow || false}
                            name="flow"
                            onClick={(e) => {
                              setAsset((prevAsset) => {
                                const updatedPrev =
                                  ensureChildrenArray(prevAsset);
                                const newSuites = [...updatedPrev.Children];
                                newSuites[thisIndex] = {
                                  ...newSuites[thisIndex],
                                  flow: e.target.checked,
                                };
                                return {
                                  ...updatedPrev,
                                  Children: newSuites,
                                };
                              });
                            }}
                          />
                        </Tooltip>
                        <IconButton
                          aria-label="delete"
                          onClick={() => {
                            const newChildren = asset.Children.filter(
                              (_, index) => index !== thisIndex
                            );
                            setAsset({ ...asset, Children: newChildren });
                          }}
                        >
                          <DeleteIcon fontSize={"small"} />
                        </IconButton>
                      </div>
                    </Stack>

                    {/* Toggle Color Picker */}
                    {isOpen && (
                      <ClickAwayListener
                        onClickAway={() => setOpenPickerIndex(null)}
                      >
                        <Box
                          className="color-picker"            
                        >
                          <SketchPicker
                            color={child.color || "#b0e0e6"}
                            onChangeComplete={(color) =>
                              handleChildColorChange(thisIndex, color.hex)
                            }
                          />
                        </Box>
                      </ClickAwayListener>
                    )}
                  </Stack>
                );
              })}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Legend Toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={asset?.showLegend !== false}
                onChange={(e) => setAsset((prev) => ({ ...prev, showLegend: e.target.checked }))}
              />
            }
            label="Show asset type legend"
          />

          <Divider sx={{ my: 2 }} />

          {/* Data Tags Section */}
          <Typography fontWeight="bold">
            Data Tags ({asset?.dataTags?.length || 0})
          </Typography>
          {hasDataTagsOrAgents && dataSource && (
            <Box sx={{ mb: 1 }}>
              <ToggleButtonGroup
                value={dataTagGroupDataSource || "inherit"}
                exclusive
                onChange={handleTagGroupDataSourceChange}
                size="small"
              >
                <ToggleButton value="inherit">
                  Factory Default ({dataSource === "live" ? "Live" : "Simulated"})
                </ToggleButton>
                <ToggleButton value="live">Live MQTT</ToggleButton>
                <ToggleButton value="simulated">Simulated</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}
          <TagEditor
            tags={asset?.dataTags || []}
            onTagsChange={handleTagsChange}
            groupDataSource={effectiveTagGroupSource}
            compact
            requireDataSource={requireDataSource}
            onReady={setTagEditorApi}
          />

          <Divider sx={{ my: 2 }} />

          {/* AI Agents Section */}
          <Typography fontWeight="bold">
            AI Agents ({asset?.agents?.length || 0})
          </Typography>
          {hasDataTagsOrAgents && dataSource && (
            <Box sx={{ mb: 1 }}>
              <ToggleButtonGroup
                value={agentGroupDataSource || "inherit"}
                exclusive
                onChange={handleAgentGroupDataSourceChange}
                size="small"
              >
                <ToggleButton value="inherit">
                  Factory Default ({dataSource === "live" ? "Live" : "Simulated"})
                </ToggleButton>
                <ToggleButton value="live">Live MQTT</ToggleButton>
                <ToggleButton value="simulated">Simulated</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}
          <AgentEditor
            agents={asset?.agents || []}
            onAgentsChange={handleAgentsChange}
            groupDataSource={effectiveAgentGroupSource}
            compact
            requireDataSource={requireDataSource}
            onReady={setAgentEditorApi}
          />
        </Stack>
      </Grid>

      {/* Data Source Prompt Dialog */}
      <Dialog
        open={dataSourcePromptOpen}
        onClose={() => {
          setDataSourcePromptOpen(false);
          setPendingAddType(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Choose Data Source</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Before adding {pendingAddType === "tag" ? "data tags" : "agents"}, please choose how this factory will receive data.
          </Typography>
          <Stack direction="row" gap={2} justifyContent="center">
            <Button
              variant="outlined"
              size="large"
              onClick={() => handleDataSourcePromptChoice("live")}
              sx={{ flex: 1, py: 3, flexDirection: "column" }}
            >
              <Typography fontWeight="bold">Live MQTT</Typography>
              <Typography variant="caption" color="textSecondary">
                Connect to a real MQTT broker
              </Typography>
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => handleDataSourcePromptChoice("simulated")}
              sx={{ flex: 1, py: 3, flexDirection: "column" }}
            >
              <Typography fontWeight="bold">Simulated</Typography>
              <Typography variant="caption" color="textSecondary">
                Use example data for demos
              </Typography>
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDataSourcePromptOpen(false);
            setPendingAddType(null);
          }}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
